import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import EmptyState from "@/components/public/EmptyState";
import SectionHeading from "@/components/public/SectionHeading";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import { MapPin } from "lucide-react";

export default function CoffeeOrigin() {
  const [origins, setOrigins] = useState([]);

  useEffect(() => {
    (async () => { try { const r = await api.get("/origins"); setOrigins(r.data); } catch (e) {} })();
  }, []);

  return (
    <div>
      <PageHero eyebrow="Indonesian Arabica from Origin" title="Coffee Origin" sub="Indonesia is one of the world's most diverse Arabica origins. We source from selected producing areas — regions are shown only when confirmed by our team." image="/company/f4.jpg" />

      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
              <img src="/company/f1.jpg" alt="Ripe Arabica coffee cherries on the tree at origin" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-brand">The Archipelago</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest-dark sm:text-4xl">Grown Across Indonesia's Highlands</h2>
            <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">
              From Sumatra to Java and beyond, Indonesian Arabica is shaped by volcanic soils, high altitudes and distinctive processing traditions such as wet-hulling (Giling Basah). Our origin details describe the producing areas we actually source from.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Sumatra", "Aceh / Gayo", "West Java", "Bali", "Flores"].map((r) => (
                <span key={r} className="inline-flex items-center gap-1.5 rounded-full border border-forest/15 bg-white px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-forest"><MapPin className="h-3 w-3" />{r}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-beige py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="Sourcing Regions" title="Our Confirmed Origins" />
          <div className="mt-12">
            {origins.length === 0 ? (
              <EmptyState message="Origin information is being updated. Confirmed sourcing regions will appear here." icon={MapPin} testId="origins-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {origins.map((o) => (
                  <Reveal key={o.id}>
                    <div className="h-full overflow-hidden rounded-xl border border-forest/10 bg-white">
                      {o.image ? <img src={o.image} alt={o.name} loading="lazy" className="aspect-[4/3] w-full object-cover" /> : <div className="flex aspect-[4/3] items-center justify-center bg-forest/5"><MapPin className="h-6 w-6 text-forest/40" /></div>}
                      <div className="p-5">
                        <h3 className="font-display text-lg font-bold text-forest-dark">{o.name}</h3>
                        <div className="mt-1 font-mono text-[11px] uppercase tracking-wider text-amber-brand">{[o.province, o.region].filter(Boolean).join(" · ")}</div>
                        {o.description && <p className="mt-3 font-body text-sm leading-relaxed text-espresso/65">{o.description}</p>}
                        <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-[11px] text-espresso/65">
                          {o.altitude && <div><span className="text-espresso/40">Altitude</span><br />{o.altitude}</div>}
                          {o.coffee_type && <div><span className="text-espresso/40">Type</span><br />{o.coffee_type}</div>}
                          {o.processing && <div><span className="text-espresso/40">Process</span><br />{o.processing}</div>}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
